import {action, computed, observable, toJS, runInAction} from 'mobx';
import unionBy from 'lodash/unionBy';
import filter from 'lodash/filter';
import clone from 'lodash/cloneDeep';
import map from 'lodash/map';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import {ITag} from '@techsee/techsee-ui-common/lib/tags-panel';
import {Nullable} from '@techsee/techsee-ui-common/lib/_shared/reusable-types';
import {IHistoryService} from '@techsee/techsee-client-infra/lib/services/HistoryService';
import {ITranslate} from '../../services/LocalizationService';
import {ISessionSummaryController, TSImageResource, IImageResource} from './SessionSummary.contracts';
import {ISessionSummaryLoggingService} from '../../services/SessionSummaryLoggingService';
import {IFieldModel, SimpleFieldModel} from '@techsee/techsee-ui-common/lib/forms/_shared/simple-field-model';
import {RestApiService, AuthType} from '@techsee/techsee-client-infra/lib/services/RestApiService';
import {IHistoryImageParams} from '../../states/history/_contracts/HistoryServiceContracts';

export class SessionSummaryController implements ISessionSummaryController {
    @observable private _screenshots: TSImageResource[] = [];

    @observable private _url: string = '';

    @observable private _isVisible: boolean = false;

    @observable private _isTagsOpen: boolean = false;

    @observable public displayErrorMessage: boolean = false;

    @observable private _currentResource: Nullable<TSImageResource> = null;

    @observable public tabIndex: number = 0;

    @observable public disabledExpand = false;

    @observable newTagToAdd: IFieldModel = new SimpleFieldModel({
        value: ''
    });

    private readonly _commentMaxLength: number;

    private readonly _allowNewSessionSummary: boolean;

    private readonly _roomId: string;

    private readonly _translate: ITranslate;

    private readonly _historyService: IHistoryService;

    private readonly _httpService: RestApiService;

    private _informationTags: ITag[] = [];

    private _eventLogger: ISessionSummaryLoggingService;

    public readonly isTablet: boolean;

    @observable public isFailureDecisionOpen = false;

    private _onSummaryCompleted: (() => void) | null = null;

    constructor(
        historyService: IHistoryService,
        eventLogger: ISessionSummaryLoggingService,
        roomId: string,
        translate: ITranslate,
        accountData: any,
        httpService: RestApiService,
        environmentService: any,
        displayTabletAsDesktop: boolean
    ) {
        this._historyService = historyService;
        this._roomId = roomId;
        this._translate = translate;
        this._eventLogger = eventLogger;
        this._httpService = httpService;

        const imageTags = accountData.imageTags || [];
        this._allowNewSessionSummary =
            get(accountData, 'protectedSettings.newSessionSummary.enable') &&
            get(accountData, 'protectedSettings.newSessionSummary.allowAddingDescriptionAndNewTags');
        this.disabledExpand = this._allowNewSessionSummary;
        this._commentMaxLength = get(accountData, 'protectedSettings.newSessionSummary.descriptionMaxLength') || 0;

        this.isTablet = environmentService.isTablet(displayTabletAsDesktop);

        this.setInformationTags(imageTags as any);
        this.setCurrentResource = this.setCurrentResource.bind(this);
        this.setIsMarked = this.setIsMarked.bind(this);
        this.setIsSelected = this.setIsSelected.bind(this);
        this.setTagsOpen = this.setTagsOpen.bind(this);
        this.addNewTag = this.addNewTag.bind(this);
        this.disableExpandButton = this.disableExpandButton.bind(this);
        this.setTabIndex = this.setTabIndex.bind(this);
        this.onChangeComment = this.onChangeComment.bind(this);
    }

    get commentMaxLength() {
        return this._commentMaxLength;
    }

    get allowNewSessionSummary() {
        return this._allowNewSessionSummary;
    }

    @action
    onChangeComment(comment: string) {
        if (this._currentResource) {
            this._currentResource.comment = comment;
        }
    }

    @action
    setTabIndex(index: number) {
        this.tabIndex = index;
    }

    @action
    disableExpandButton(value: boolean) {
        this.disabledExpand = value;
    }

    @action
    addNewTag() {
        if (this.newTagToAdd && this.newTagToAdd.value) {
            const newTag: ITag = {
                id: this.newTagToAdd.value.toString(),
                text: this.newTagToAdd.value.toString(),
                className: 'selected'
            };

            map(this._screenshots, (screenshot: TSImageResource) => {
                if (screenshot._id === (this._currentResource && this._currentResource._id)) {
                    if (!screenshot.informationTags) {
                        screenshot.informationTags = [];
                    }

                    const tagExists = find(screenshot.informationTags, {id: newTag.id});

                    if (!tagExists) {
                        screenshot.informationTags.push(newTag);
                        this._currentResource = screenshot;
                    } else {
                        tagExists.className = 'selected';
                    }
                }
            });

            this._screenshots = observable.array(this._screenshots, {deep: true});

            this.newTagToAdd.setValue('');
        }
    }

    get translate() {
        return this._translate;
    }

    @computed
    get countImageMarked() {
        return this._screenshots.filter((image) => image.isMarked).length;
    }

    @computed
    get screenshots() {
        return this._screenshots;
    }

    @computed
    get comment() {
        return (this._currentResource && this._currentResource.comment) || '';
    }

    @computed
    get currentResource() {
        return this._currentResource;
    }

    @computed
    get isSummaryVisible() {
        return this._isVisible;
    }

    @computed
    get isTagsOpen() {
        return this._isTagsOpen;
    }

    @computed
    get informationTags() {
        return this._informationTags;
    }

    @action
    setTagsOpen() {
        this._isTagsOpen = !this._isTagsOpen;
    }

    @action
    setCurrentResource(image: TSImageResource) {
        this._currentResource = image;
    }

    @action
    setScreenShots(screenshots: TSImageResource[]) {
        const resources = this.adjustScreenshots(screenshots).reverse();

        this._screenshots = observable.array(resources, {deep: true});

        if (this._screenshots && this._screenshots.length > 0) {
            this.setCurrentResource(this._screenshots[0]);
        }
    }

    @action
    showSessionSummary() {
        this._isVisible = true;
        this._eventLogger.log({description: 'Getting resources from history...'});

        this.getAllResourceFromHistory()
            .then((historyRecord: any) => {
                this._eventLogger.log({description: 'Setting screenshots'});
                // getHistoryByRoomIdOrCustomerId returns response.data directly (IImageResource)
                const imageResource = historyRecord as IImageResource;
                this.setScreenShots(imageResource && imageResource.images);
            })
            .catch((err) => {
                this._eventLogger.log({description: 'Getting resources from history failed!', err});

                runInAction(() => {
                    this.displayErrorMessage = true;
                });
            });
    }

    @action
    hideSessionSummary() {
        this._isVisible = false;
        this._isTagsOpen = false;
        this._currentResource = null;
        this._screenshots = [];

        this._eventLogger.log({description: 'Closing session summary'});
    }

    private getDataForSaving(item: TSImageResource, saveAll: boolean): IHistoryImageParams {
        const comment = item.comment;
        const dataForSaving: IHistoryImageParams = {data: {comment}};

        if (saveAll) {
            const isMarked = item.isMarked;
            const tags = filter(item.informationTags, (tag: ITag) => tag.className === 'selected');

            tags.map((tag: ITag) => delete tag.className);

            dataForSaving.data.isMarked = isMarked;
            dataForSaving.data.tags = tags;
        }

        return dataForSaving;
    }

    async saveSessionData(saveAll: boolean): Promise<void> {
        this._eventLogger.log({description: 'Saving session summary...', saveAll});

        const promises: any = [];

        forEach(this.screenshots, (item) => {
            const jsItem = toJS(item);
            const tags = filter(item.informationTags, (tag: ITag) => tag.className === 'selected');

            tags.map((tag: ITag) => delete tag.className);

            const updateItemPromise = Promise.resolve()
                .then(() => this.getDataForSaving(jsItem, saveAll))
                .then(async (data) => {
                    const tags = data.data.tags || [];
                    const isMarked = data.data.isMarked || false;
                    const comment = data.data.comment;

                    // Make a single HTTP call with all fields including comment
                    const url = `/api/history/IMAGE/${item._id}/update`;
                    const body: any = {
                        tags: tags,
                        isMarked: isMarked
                    };

                    // Include comment if it exists
                    if (comment !== undefined) {
                        body.comment = comment;
                    }

                    await this._httpService.put(url, body, undefined, undefined, AuthType.Header);
                });

            promises.push(updateItemPromise);
        });

        try {
            await Promise.all(promises);

            this._eventLogger.log({description: 'Done saving session summary'});
        } catch (err) {
            this._eventLogger.log({description: 'Failed saving session summary!', err});

            throw err;
        }
    }

    @computed
    get badgeCount() {
        const selectedTags = filter(
            this.currentResource && this.currentResource.informationTags,
            (tag: ITag) => tag.className === 'selected'
        );

        return selectedTags.length && selectedTags.length;
    }

    @action
    setIsMarked(index: number) {
        if (index > -1) {
            this._screenshots[index].isMarked = !this._screenshots[index].isMarked;
        }
    }

    @action
    setIsSelected(index: number) {
        if (index > -1) {
            this._screenshots.map((screenshot) => (screenshot.isSelected = false));
            this._screenshots[index].isSelected = true;
        }
    }

    @action
    setSelectDeselectAll(value: boolean) {
        this._screenshots.forEach((item) => {
            item.isMarked = value;
        });
    }

    private getAllResourceFromHistory() {
        return this._historyService.getHistoryByRoomIdOrCustomerId(this._roomId);
    }

    @action
    private setInformationTags(imageTags: ITag[]) {
        this._informationTags = clone(imageTags);

        this._informationTags.forEach((tag) => {
            tag.id = tag.text;
            tag.className = '';
        });
    }

    private adjustScreenshots(screenshots: TSImageResource[]) {
        const resources = clone(screenshots);

        resources.forEach((image: TSImageResource, index: number) => {
            const tags: ITag[] = [];

            if (image.tags) {
                image.tags.forEach((tag) => {
                    tags.push({text: tag.text, id: tag.text, className: 'selected'});
                });
            }

            image.comment = image.comment || '';
            image.isSelected = false;
            image.tags = tags;
            image.informationTags = unionBy(tags, clone(this._informationTags), 'id');

            if (!image.isMarked) {
                image.isMarked = false;
            }

            if (index === resources.length - 1) {
                image.isSelected = true;
            }
        });

        return resources;
    }

    @action
    OpenFailureDecisionModal() {
        this.isFailureDecisionOpen = true;

        this._eventLogger.log('Showing a decision popup for the user how to progress since summary saving has failed');
    }

    @action
    setFailureDecision(decisionToWait: boolean) {
        this.isFailureDecisionOpen = false;

        if (decisionToWait) {
            this._eventLogger.log('User decided to abort the summary saving to failure to save the summary results');
        } else {
            this._eventLogger.log(
                'User decided to close the summary regardless of failure to save the summary results'
            );
        }
    }

    setOnSummaryCompleted = (callback: () => void): void => {
        this._onSummaryCompleted = callback;
    };

    onSummaryCompleted = (): void => {
        if (this._onSummaryCompleted) {
            this._onSummaryCompleted();
        }
    };
}
